import { Link } from "react-router-dom";
import ToastHelper from "@/utils/toastHelper";
import { Download, XCircle, MapPin, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { STATUS_STYLES } from "@/data/mockData";
import apiClient from "@/services/apiClient";
import { formatCalendarDate } from "@/utils/dateUtils";

export default function StudentBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
        const response = await apiClient.get(`${apiBaseUrl}/StudentBooking/MyBookings`);
        
        console.log("API Response:", response);
        console.log("Response Data:", response.data);
        
        // Extract bookings array from API response and map to component format
        const bookingsData = response.data?.bookings || [];
        const mappedBookings = bookingsData.map((b) => ({
          id: b.studentBookingId,
          paymentTransactionId: b.paymentTransactionId,
          bookingNumber: b.bookingNumber,
          status: b.bookingStatus,
          paid: b.paymentStatus === "Paid", 
          date: b.scheduleDate,
          scheduleCode: b.scheduleCode,
          startTime: b.startTime,
          endTime: b.endTime,
          courseId: b.courseId,
          courseCode: b.courseCode,
          courseName: b.courseName,
          courseImage: b.courseImage,
          campusName: b.campusName,
          courseFee: b.courseFee,
          gst: b.gst,
          discount: b.discount,
          totalAmount: b.totalAmount,
          paymentMethod: b.paymentMethod,
          receiptNumber: b.receiptNumber,
          invoiceNumber: b.invoiceNumber,
          paidDate: b.paidDate,
          canCancel: b.canCancel,
        }));
        
        setBookings(Array.isArray(mappedBookings) ? mappedBookings : []);
        console.log("Bookings set to:", mappedBookings);
      } catch (err) {
        console.error("Failed to fetch bookings:", err);
        console.error("Error response:", err.response);
        setError(err.message);
        ToastHelper.error("Failed to load bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const cancel = (id) => ToastHelper.success(`Booking ${id} cancelled - refund processed`);
  const receipt = async (booking) => {
    if (!booking.paymentTransactionId) return ToastHelper.error("Receipt is not available for this booking.");
    try {
      const response = await apiClient.get(`/StudentBooking/${booking.paymentTransactionId}/receipt`, { responseType: "blob" });
      const fileUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `${booking.receiptNumber || booking.bookingNumber || "payment-receipt"}.pdf`;
      link.click();
      window.URL.revokeObjectURL(fileUrl);
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to download the receipt.");
    }
  };

  return (
    <div className="space-y-6" data-testid="student-bookings">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">My bookings</h1>
        <Link to="/calendar" className="wc-btn-primary text-sm">Book new class</Link>
      </div>

      {loading && <p className="text-slate-500">Loading bookings...</p>}
      {error && <p className="text-rose-600">Error: {error}</p>}
      {!loading && bookings.length === 0 && <p className="text-slate-500">No bookings yet</p>}

      {bookings.map((b) => {
        const timeRange = `${b.startTime} - ${b.endTime}`;
        return (
          <div key={b.id} className="wc-card flex flex-col md:flex-row gap-4 items-start" data-testid={`booking-${b.id}`}>
            <div className="w-16 h-16 rounded-lg bg-[#0A2540] text-white flex flex-col items-center justify-center shrink-0">
              <div className="text-[10px] uppercase font-semibold text-orange-300">{formatCalendarDate(b.date, { month: "short" })}</div>
              <div className="text-2xl font-display font-extrabold leading-none">{formatCalendarDate(b.date, { day: "numeric" })}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-extrabold text-[#0A2540]">{b.courseName} · {b.courseCode}</div>
                <span className={`wc-badge ${STATUS_STYLES[b.status] || ""}`}>{b.status}</span>
              </div>
              <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{timeRange}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{b.campusName}</span>
                <span>Booking ID: {b.id}</span>
                <span>Paid: ${b.courseFee}</span>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => receipt(b)} className="wc-btn-outline text-xs inline-flex items-center gap-1" data-testid={`receipt-${b.id}`}><Download className="w-3.5 h-3.5" /> Receipt PDF</button>
              {b.canCancel && <button onClick={() => cancel(b.id)} className="text-xs font-semibold text-rose-600 hover:bg-rose-50 border border-rose-200 px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid={`cancel-${b.id}`}><XCircle className="w-3.5 h-3.5" /> Cancel</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
